import { useMemo, useState } from "react";
import PropTypes from "prop-types";
import { FormattedMessage } from "react-intl";
import messages from "./messages";
import { Grid, Popover, Typography } from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import moment from "moment";
import RefrezhButton from "components/RefrezhButton";
import { withRouter } from "react-router";
import InfoIcon from "basicComponents/Icons/InfoIcon";
import Base from "components/Message/Base";

const useStyles = makeStyles(theme => ({
  header: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
  },
  headerTitle: {
    display: "flex",
    alignItems: "center",
    "& > *:not(:first-child)": {
      marginLeft: theme.spacing(1),
    },
  },
  poDetails: {
    borderBottom: "1px solid rgba(0,0,0,0.46)",
    paddingBottom: theme.spacing(2),
  },
  content: {
    padding: theme.spacing(2, 0),
  },
  actionContainer: {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    borderTop: "1px solid rgba(0,0,0,0.46)",
    padding: theme.spacing(2),
  },
  marginTop: {
    marginTop: theme.spacing(2),
  },
  marginSides: {
    margin: theme.spacing(0, 2),
  },
  popover: {
    pointerEvents: "none",
  },
  paper: {
    padding: theme.spacing(1),
    borderRadius: "15px",
    border: "1px solid black",
    maxWidth: "300px",
    lineHeight: 1,
  },

  stlConfirm: {
    marginLeft: "auto",
    marginRight: "auto",
    textAlign: "center",
  },
  confirmButton: {
    marginTop: theme.spacing(2),
  },
}));

function UploadSTL({
  activeMessage = {},
  onClose,
  history,
  showPatient = false,
}) {
  const classes = useStyles();
  const [anchorEl, setAnchorEl] = useState(null);

  const handlePopoverOpen = event => {
    setAnchorEl(event.currentTarget);
  };

  const handlePopoverClose = () => {
    setAnchorEl(null);
  };

  const open = Boolean(anchorEl);

  const body = useMemo(
    () => (
      <div className={classes.body}>
        <Typography variant="body1">
          <FormattedMessage
            {...messages.upload_stl_message}
            values={{
              caseNumber: activeMessage.productPlan.caseId,
            }}
          />
          <span
            style={{ marginLeft: "5px", display: "inline-flex" }}
            data-cy="popupSTL"
            aria-haspopup="true"
            onMouseEnter={handlePopoverOpen}
            onMouseLeave={handlePopoverClose}
          >
            <InfoIcon size={15} />
          </span>
        </Typography>
        <div className={classes.poDetails}>
          <div className={classes.stlConfirm} data-cy="STLButton">
            <RefrezhButton
              disabled={activeMessage.status === 2}
              onClick={() => {
                history.push(
                  `/order/patient/${activeMessage.patient.id}/treatment/${
                    activeMessage.productOrder.id
                  }/clinical`,
                );
              }}
              className={classes.confirmButton}
            >
              {activeMessage.status === 2 ? (
                <FormattedMessage
                  {...messages.upload_stl_done}
                  values={{
                    date: moment
                      .unix(activeMessage.updatedAt)
                      .format("DD-MM-YYYY"),
                  }}
                />
              ) : (
                <FormattedMessage {...messages.upload_stl_confirm} />
              )}
            </RefrezhButton>
          </div>
        </div>
        <Grid container className={classes.content}>
          <Grid item xs={12} sm={6}>
            <Typography variant="caption">
              <FormattedMessage {...messages.order_placed_upper_product} />
            </Typography>
            <Typography variant="subtitle1">
              {activeMessage.productOrder.upperProductName}
            </Typography>
          </Grid>
          <Grid item xs={12} sm={6}>
            <Typography variant="caption">
              <FormattedMessage {...messages.order_placed_lower_product} />
            </Typography>
            <Typography variant="subtitle1">
              {activeMessage.productOrder.lowerProductName}
            </Typography>
          </Grid>
        </Grid>
        <Popover
          open={open}
          anchorEl={anchorEl}
          className={classes.popover}
          classes={{
            paper: classes.paper,
          }}
          anchorOrigin={{
            vertical: "bottom",
            horizontal: "center",
          }}
          transformOrigin={{
            vertical: "top",
            horizontal: "center",
          }}
          onClose={handlePopoverClose}
          disableRestoreFocus
        >
          <Typography variant="caption">
            <FormattedMessage {...messages.upload_stl_info} />
          </Typography>
        </Popover>
      </div>
    ),
    [
      activeMessage.patient,
      activeMessage.productOrder.id,
      activeMessage.productOrder.lowerProductName,
      activeMessage.productOrder.upperProductName,
      activeMessage.productPlan.caseId,
      activeMessage.status,
      activeMessage.updatedAt,
      anchorEl,
      classes.body,
      classes.confirmButton,
      classes.content,
      classes.paper,
      classes.poDetails,
      classes.popover,
      classes.stlConfirm,
      history,
      open,
    ],
  );

  return (
    <Base
      activeMessage={activeMessage}
      shouldRenderPatient={showPatient}
      onClose={onClose}
      title={<FormattedMessage {...messages.upload_stl_title} />}
    >
      {body}
    </Base>
  );
}

UploadSTL.propTypes = {
  activeMessage: PropTypes.shape({
    stlFileURL: PropTypes.string,
    updatedAt: PropTypes.number,
    productOrder: PropTypes.shape({
      lowerProductName: PropTypes.string,
      upperProductName: PropTypes.string,
      phase: PropTypes.string,
      avadentCaseId: PropTypes.string,
      deliveryDate: PropTypes.number,
    }),
    productPlan: PropTypes.shape({
      caseId: PropTypes.string,
    }),
    patient: PropTypes.shape({
      id: PropTypes.number,
    }),
  }),
  onClose: PropTypes.func.isRequired,
  onCloseMessage: PropTypes.func.isRequired,
  showPatient: PropTypes.bool,
};

export default withRouter(UploadSTL);
